/**
 * Thin fetch wrapper for talking to /auth/* and /api/*.
 *
 * Every response is unpacked from the `{ ok, data }` / `{ ok, error }`
 * envelope. On `{ ok: false }` an ApiError is thrown so React Query can
 * surface it.
 *
 * The `X-Reauthenticate` response header (set by requireSession on 401
 * after a refresh failure) triggers a top-level redirect to /auth/initiate
 * so the user re-authenticates with the IDP.
 */

export class ApiError extends Error {
  status: number;
  code: string;
  details: unknown;
  constructor(status: number, code: string, message: string, details: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type ApiResponse<T> =
  | { ok: true; data: T }
  | { ok: false; error: { code: string; message: string; details?: unknown } };

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });

  if (res.headers.get('X-Reauthenticate') === '1') {
    window.location.assign('/auth/initiate');
    throw new ApiError(401, 'UNAUTHENTICATED', 'reauth_required');
  }

  // /auth/me returns a bare payload (not enveloped) — handle separately.
  if (path === '/auth/me') {
    if (!res.ok) throw new ApiError(res.status, 'INTERNAL', `auth_me_failed:${res.status}`);
    return (await res.json()) as T;
  }

  let body: ApiResponse<T>;
  try {
    body = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError(res.status, 'INTERNAL', `non_json_response:${res.status}`);
  }
  if (!('ok' in body)) {
    throw new ApiError(res.status, 'INTERNAL', 'invalid_envelope');
  }
  if (!body.ok) {
    throw new ApiError(res.status, body.error.code, body.error.message, body.error.details);
  }
  return body.data;
}

export const api = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'DELETE',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),
};
